import { useCallback, useEffect, useRef, useState } from 'react';
import type { SessionCallTranscriptionChatMessage } from '@/components/session-call/sessionCallTranscriptionChat';

const DEEPGRAM_SPEAKER_ID = 'deepgram-local';
const LIVE_PREVIEW_MESSAGE_ID = `${DEEPGRAM_SPEAKER_ID}-live-preview`;

const createDeepgramTranscriptionMessage = (
	text: string,
	options?: { isLive?: boolean; id?: string },
): SessionCallTranscriptionChatMessage => ({
	id:
		options?.id ??
		`${DEEPGRAM_SPEAKER_ID}-${Date.now()}-${text.slice(0, 24)}`,
	participantId: DEEPGRAM_SPEAKER_ID,
	speakerName: 'Profissional',
	isLocalSpeaker: true,
	text,
	timestamp: new Date().toISOString(),
	isLive: options?.isLive ?? false,
});

const withoutLiveMessages = (
	messages: SessionCallTranscriptionChatMessage[],
): SessionCallTranscriptionChatMessage[] =>
	messages.filter((message) => !message.isLive);

type UseDeepgramTranscriptionChatMessagesOptions = {
	lastFinalSegment: string;
	finalSegmentCounter: number;
	interimTranscript: string;
	enabled: boolean;
};

const useDeepgramTranscriptionChatMessages = ({
	lastFinalSegment,
	finalSegmentCounter,
	interimTranscript,
	enabled,
}: UseDeepgramTranscriptionChatMessagesOptions) => {
	const [messages, setMessages] = useState<
		SessionCallTranscriptionChatMessage[]
	>([]);
	const [liveCaptureEnabled, setLiveCaptureEnabled] = useState(true);
	const [stoppedNotice, setStoppedNotice] = useState<string | null>(null);
	const processedSegmentCounterRef = useRef(0);
	const prevInterimRef = useRef('');

	useEffect(() => {
		if (!enabled || !liveCaptureEnabled || finalSegmentCounter === 0)
			return;
		if (processedSegmentCounterRef.current === finalSegmentCounter) return;

		processedSegmentCounterRef.current = finalSegmentCounter;

		const segment = lastFinalSegment.trim();
		if (!segment) return;

		prevInterimRef.current = '';

		setMessages((current) => [
			...withoutLiveMessages(current),
			createDeepgramTranscriptionMessage(segment, {
				id: `${DEEPGRAM_SPEAKER_ID}-final-${finalSegmentCounter}`,
			}),
		]);
	}, [enabled, finalSegmentCounter, lastFinalSegment, liveCaptureEnabled]);

	useEffect(() => {
		if (!enabled || !liveCaptureEnabled) return;

		const interim = interimTranscript.trim();
		if (!interim) {
			prevInterimRef.current = '';
			setMessages((current) => withoutLiveMessages(current));
			return;
		}

		if (interim === prevInterimRef.current) return;
		prevInterimRef.current = interim;

		setMessages((current) => {
			const committed = withoutLiveMessages(current);
			return [
				...committed,
				createDeepgramTranscriptionMessage(interim, {
					isLive: true,
					id: LIVE_PREVIEW_MESSAGE_ID,
				}),
			];
		});
	}, [enabled, interimTranscript, liveCaptureEnabled]);

	const clearMessages = useCallback(() => {
		processedSegmentCounterRef.current = 0;
		prevInterimRef.current = '';
		setMessages([]);
	}, []);

	const stopLiveCapture = useCallback((notice: string) => {
		setMessages((current) => withoutLiveMessages(current));
		setLiveCaptureEnabled(false);
		setStoppedNotice(notice);
	}, []);

	const resumeLiveCapture = useCallback(() => {
		setLiveCaptureEnabled(true);
		setStoppedNotice(null);
	}, []);

	return {
		messages,
		liveCaptureEnabled,
		stoppedNotice,
		clearMessages,
		stopLiveCapture,
		resumeLiveCapture,
	};
};

export { useDeepgramTranscriptionChatMessages };
